import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError, formatRM } from "@/lib/api";
import { toast } from "sonner";
import { Plus, KeyRound, Edit, X, Loader2 } from "lucide-react";

function UserForm({ initial, onCancel, onSaved }) {
  const { t } = useTranslation();
  const isNew = !initial?.id;
  const [form, setForm] = useState({
    email: initial?.email || "",
    password: "",
    name: initial?.name || "",
    phone: initial?.phone || "",
    role: initial?.role || "member",
    agent_status: initial?.agent_status || "approved",
  });
  const [saving, setSaving] = useState(false);
  const inputCls = "w-full px-4 py-2.5 rounded-md border border-[#e5ebeb] focus:border-[#00868e] outline-none bg-white transition-colors duration-200";

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/admin/users", { ...form });
      } else {
        const upd = { name: form.name, phone: form.phone, role: form.role };
        if (form.role === "agent") upd.agent_status = form.agent_status;
        await api.patch(`/admin/users/${initial.id}`, upd);
      }
      toast.success(t("common.saved"));
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="rounded-lg border border-[#e5ebeb] bg-white p-6 space-y-4" data-testid="user-form">
      <div className="flex items-center justify-between">
        <h2 className="font-serif-cn text-xl text-[#003645] font-semibold">
          {isNew ? t("adminP2.createUser") : t("adminP2.editUser")}
        </h2>
        <button type="button" onClick={onCancel} className="text-[#4a6e77]"><X size={16} /></button>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-[#4a6e77] mb-1.5">{t("auth.name")}</label>
          <input required className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="uf-name" />
        </div>
        <div>
          <label className="block text-xs text-[#4a6e77] mb-1.5">{t("auth.email")}</label>
          <input required type="email" disabled={!isNew} className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="uf-email" />
        </div>
        {isNew && (
          <div>
            <label className="block text-xs text-[#4a6e77] mb-1.5">{t("auth.password")}</label>
            <input required minLength={6} type="password" className={inputCls} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="uf-password" />
          </div>
        )}
        <div>
          <label className="block text-xs text-[#4a6e77] mb-1.5">{t("auth.phone")}</label>
          <input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="uf-phone" />
        </div>
        <div>
          <label className="block text-xs text-[#4a6e77] mb-1.5">{t("adminP2.role")}</label>
          <select className={inputCls} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="uf-role">
            <option value="member">{t("auth.member")}</option>
            <option value="agent">{t("auth.agent")}</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        {form.role === "agent" && (
          <div>
            <label className="block text-xs text-[#4a6e77] mb-1.5">{t("adminP2.agentStatus")}</label>
            <select className={inputCls} value={form.agent_status} onChange={(e) => setForm({ ...form, agent_status: e.target.value })} data-testid="uf-agent-status">
              <option value="pending">pending</option>
              <option value="approved">approved</option>
              <option value="rejected">rejected</option>
            </select>
          </div>
        )}
      </div>
      <div className="flex gap-2 pt-2">
        <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#005870] text-white font-medium hover:bg-[#003645] disabled:opacity-60" data-testid="uf-save">
          {saving && <Loader2 size={14} className="animate-spin" />}{t("common.save")}
        </button>
        <button type="button" onClick={onCancel} className="px-5 py-2 rounded-full border border-[#e5ebeb] text-[#003645]">{t("common.cancel")}</button>
      </div>
    </form>
  );
}

function PasswordForm({ user, onCancel, onSaved }) {
  const { t } = useTranslation();
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/admin/users/${user.id}/password`, { new_password: pw });
      toast.success(t("common.saved"));
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="rounded-lg border border-[#e5ebeb] bg-white p-6 space-y-4" data-testid="password-form">
      <h2 className="font-serif-cn text-xl text-[#003645] font-semibold">
        {t("adminP2.resetPassword")} — {user.email}
      </h2>
      <input type="password" required minLength={6} placeholder={t("adminP2.newPassword")} value={pw} onChange={(e) => setPw(e.target.value)} className="w-full px-4 py-2.5 rounded-md border border-[#e5ebeb] focus:border-[#00868e] outline-none" data-testid="pw-input" />
      <div className="flex gap-2">
        <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#005870] text-white font-medium hover:bg-[#003645] disabled:opacity-60" data-testid="pw-save">
          {saving && <Loader2 size={14} className="animate-spin" />}{t("common.save")}
        </button>
        <button type="button" onClick={onCancel} className="px-5 py-2 rounded-full border border-[#e5ebeb] text-[#003645]">{t("common.cancel")}</button>
      </div>
    </form>
  );
}

export default function AdminUsers() {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} for new | user obj
  const [resetting, setResetting] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => setUsers([])).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const setAgentStatus = async (id, status) => {
    try {
      await api.patch(`/admin/users/${id}/agent-status`, { status });
      toast.success(t("common.saved"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-users-page">
      <div className="flex items-center justify-between">
        <h1 className="font-serif-cn text-3xl text-[#003645] font-semibold">{t("admin.users")}</h1>
        {!editing && !resetting && (
          <button onClick={() => setEditing({})} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#005870] text-white text-sm font-medium hover:bg-[#003645] transition-colors duration-200" data-testid="new-user-btn">
            <Plus size={14} /> {t("adminP2.createUser")}
          </button>
        )}
      </div>

      {editing && (
        <div className="mt-6">
          <UserForm initial={editing.id ? editing : null} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
        </div>
      )}
      {resetting && (
        <div className="mt-6">
          <PasswordForm user={resetting} onCancel={() => setResetting(null)} onSaved={() => setResetting(null)} />
        </div>
      )}

      {!editing && !resetting && (
        <div className="mt-6 overflow-x-auto">
          {loading ? (
            <div className="text-[#4a6e77]">{t("common.loading")}</div>
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-[#4a6e77]">
                  <th className="py-3 px-4 border-b border-[#e5ebeb]">Name</th>
                  <th className="py-3 px-4 border-b border-[#e5ebeb]">Email</th>
                  <th className="py-3 px-4 border-b border-[#e5ebeb]">Role</th>
                  <th className="py-3 px-4 border-b border-[#e5ebeb]">Agent</th>
                  <th className="py-3 px-4 border-b border-[#e5ebeb]">Points</th>
                  <th className="py-3 px-4 border-b border-[#e5ebeb]"></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}>
                    <td className="py-3 px-4 border-b border-[#e5ebeb] text-[#003645]">{u.name}</td>
                    <td className="py-3 px-4 border-b border-[#e5ebeb] text-[#4a6e77]">{u.email}</td>
                    <td className="py-3 px-4 border-b border-[#e5ebeb] text-[#003645]">{u.role}</td>
                    <td className="py-3 px-4 border-b border-[#e5ebeb] text-[#4a6e77]">
                      {u.agent_status || "—"}
                      {u.referral_code && <div className="text-xs font-mono tracking-wider">{u.referral_code}</div>}
                    </td>
                    <td className="py-3 px-4 border-b border-[#e5ebeb] text-[#005870] font-medium">{formatRM(u.points_balance || 0)}</td>
                    <td className="py-3 px-4 border-b border-[#e5ebeb]">
                      <div className="flex gap-2 items-center">
                        <button onClick={() => setEditing(u)} className="text-[#005870] hover:text-[#00868e]" title={t("common.edit")} data-testid={`edit-user-${u.id}`}>
                          <Edit size={14} />
                        </button>
                        <button onClick={() => setResetting(u)} className="text-[#4a6e77] hover:text-[#003645]" title={t("adminP2.resetPassword")} data-testid={`reset-pw-${u.id}`}>
                          <KeyRound size={14} />
                        </button>
                        {u.role === "agent" && u.agent_status !== "approved" && (
                          <button onClick={() => setAgentStatus(u.id, "approved")} className="px-2 py-0.5 rounded-full bg-[#005870] text-white text-[10px] font-medium hover:bg-[#003645]" data-testid={`approve-agent-${u.id}`}>
                            {t("adminP2.approve")}
                          </button>
                        )}
                        {u.role === "agent" && u.agent_status !== "rejected" && (
                          <button onClick={() => setAgentStatus(u.id, "rejected")} className="px-2 py-0.5 rounded-full border border-red-300 text-red-700 text-[10px] font-medium hover:bg-red-50" data-testid={`reject-agent-${u.id}`}>
                            {t("adminP2.reject")}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
